import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Switch, 
  FormControlLabel, 
  Alert, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import axios from 'axios';
import { useTeam } from '../../../../shared/context/teamContext';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../shared/services/api/axiosInstance';

// Cryptique Design System
const styles = {
  primarycolor: "#0f172a",
  primaryColor: "#0f172a",
  backgroundColor: "#ffffff",
  textPrimary: "#0f172a",
  textSecondary: "#666666",
  headingFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 600 },
  bodyFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 400 }
};

const WalletEnhancementConfig = ({ selectedTeamId }) => {
  const { selectedTeam } = useTeam();
  const navigate = useNavigate();
  const [autoDeductEnabled, setAutoDeductEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const [success, setSuccess] = useState(null);

  const API_URL = process.env.REACT_APP_API_SERVER_URL || 'https://backend.cryptique.io';
  const teamId = selectedTeamId || selectedTeam?.id || selectedTeam?._id;
  const isOwner = selectedTeam?.isOwner || false;

  // Fetch auto-deduct setting on mount
  useEffect(() => {
    const fetchAutoDeductSetting = async () => {
      if (!teamId) return;
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/credits/auto-deduct`, {
          params: { teamId },
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          withCredentials: true
        });
        if (response.data?.success && response.data?.autoDeduct) {
          setAutoDeductEnabled(response.data.autoDeduct.enabled);
        }
      } catch (error) {
        console.error('Error fetching auto-deduct setting:', error);
      }
    };
    fetchAutoDeductSetting();
  }, [teamId, API_URL]);

  const handleToggle = async (event) => {
    const newValue = event.target.checked;
    if (!teamId) {
      setError('No team selected');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/credits/auto-deduct`,
        { enabled: newValue, teamId },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          withCredentials: true
        }
      );
      if (response.data?.success) {
        setAutoDeductEnabled(newValue);
      } else {
        throw new Error(response.data?.message || 'Failed to update setting');
      }
    } catch (error) {
      console.error('Error updating auto-deduct setting:', error);
      setError(error.response?.data?.message || error.message || 'Failed to update setting');
      setAutoDeductEnabled(!newValue);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async () => {
    if (!teamId) {
      setError('No team selected');
      return;
    }

    setSettingDefault(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axiosInstance.post('/team/user/default-team', {
        teamId: teamId
      });

      if (response.data) {
        setSuccess('Project set as default successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      console.error('Error setting default team:', error);
      setError(error.response?.data?.message || 'Failed to set default project');
    } finally {
      setSettingDefault(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamId) {
      setError('No team selected');
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await axiosInstance.delete(`/team/${teamId}`);

      // Clear team data from localStorage
      localStorage.removeItem('selectedTeam');
      localStorage.removeItem('selectedTeamData');
      sessionStorage.removeItem('userTeams');

      // Navigate to a safe page (or login if no teams left)
      window.location.href = '/login';
    } catch (error) {
      console.error('Error deleting team:', error);
      setError(error.response?.data?.message || 'Failed to delete project');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Typography
        variant="h6"
        sx={{
          ...styles.headingFont,
          color: styles.textPrimary,
          fontSize: '1.25rem',
          mb: 3
        }}
      >
        Wallet Enhancement
      </Typography>

      <Box
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          p: 2.5,
          backgroundColor: styles.backgroundColor,
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                ...styles.bodyFont,
                color: styles.textPrimary,
                fontSize: '1rem',
                mb: 0.5
              }}
            >
              Auto
            </Typography>
            <Typography
              sx={{
                ...styles.bodyFont,
                color: styles.textSecondary,
                fontSize: '0.875rem'
              }}
            >
              Credits will be automatically deducted when new wallets are detected on any website
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={autoDeductEnabled}
                onChange={handleToggle}
                disabled={loading}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: styles.primaryColor,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: styles.primaryColor,
                  },
                }}
              />
            }
            label={autoDeductEnabled ? 'Auto' : 'Disable'}
            sx={{
              ...styles.bodyFont,
              ml: 1.5,
              '& .MuiFormControlLabel-label': {
                color: autoDeductEnabled ? styles.primaryColor : styles.textSecondary,
                fontWeight: 500,
                fontSize: '0.9375rem'
              }
            }}
          />
        </Box>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mt: 2, fontSize: '0.875rem', py: 1 }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert 
            severity="success" 
            sx={{ mt: 2, fontSize: '0.875rem', py: 1 }} 
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}
      </Box>

      {/* Project Section */}
      <Typography
        variant="h6"
        sx={{
          ...styles.headingFont,
          color: styles.textPrimary,
          fontSize: '1.25rem',
          mb: 2,
          mt: 4
        }}
      >
        Project
      </Typography>

      <Box
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          p: 2.5,
          backgroundColor: styles.backgroundColor
        }}
      >
        {/* Make this project default */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isOwner ? 2 : 0, pb: isOwner ? 2 : 0, borderBottom: isOwner ? '1px solid #E5E7EB' : 'none' }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                ...styles.bodyFont,
                color: styles.textPrimary,
                fontSize: '1rem',
                mb: 0.5
              }}
            >
              Make this project default
            </Typography>
            <Typography
              sx={{
                ...styles.bodyFont,
                color: styles.textSecondary,
                fontSize: '0.875rem'
              }}
            >
              Set this project as your default when you log in
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleSetDefault}
            disabled={settingDefault}
            sx={{
              backgroundColor: '#4b57aa',
              color: '#fff',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              px: 2,
              py: 0.75,
              borderRadius: '6px',
              minWidth: '120px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#4b57aa',
                opacity: 0.9,
                boxShadow: 'none'
              },
              '&:disabled': {
                backgroundColor: '#DDE2EE',
                color: '#475569',
                boxShadow: 'none'
              }
            }}
          >
            {settingDefault ? 'Setting...' : 'Set Default'}
          </Button>
        </Box>

        {/* Delete project - only for owners */}
        {isOwner && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  ...styles.bodyFont,
                  color: styles.textPrimary,
                  fontSize: '1rem',
                  mb: 0.5
                }}
              >
                Delete project
              </Typography>
              <Typography
                sx={{
                  ...styles.bodyFont,
                  color: styles.textSecondary,
                  fontSize: '0.875rem'
                }}
              >
                Permanently delete this project and all its data
              </Typography>
            </Box>
            <Box
              component="button"
              onClick={() => !deleting && setDeleteDialogOpen(true)}
              disabled={deleting}
              sx={{
                backgroundColor: deleting ? '#D1D5DB' : '#b91c1c',
                color: deleting ? '#9CA3AF' : 'white',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                px: 2,
                py: 0.75,
                borderRadius: '9999px',
                minWidth: '120px',
                border: 'none',
                cursor: deleting ? 'not-allowed' : 'pointer',
                fontFamily: styles.bodyFont.fontFamily,
                display: 'inline-block',
                '&:hover': {
                  backgroundColor: deleting ? '#D1D5DB' : '#991B1B'
                },
                '&:active': {
                  backgroundColor: '#b91c1c'
                },
                '&:disabled': {
                  backgroundColor: '#D1D5DB',
                  color: '#475569',
                  cursor: 'not-allowed'
                }
              }}
            >
              Delete
            </Box>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            ...styles.headingFont,
            color: styles.textPrimary,
            fontSize: '1rem'
          }}
        >
          Delete Project
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              ...styles.bodyFont,
              color: styles.textPrimary,
              fontSize: '0.875rem',
              mb: 1
            }}
          >
            Are you sure you want to delete this project? This action cannot be reversed.
          </DialogContentText>
          <DialogContentText
            sx={{
              ...styles.bodyFont,
              color: styles.textSecondary,
              fontSize: '0.875rem'
            }}
          >
            All user, event, session and transaction data will be lost if you delete this project/team.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            sx={{
              ...styles.bodyFont,
              color: styles.textSecondary,
              textTransform: 'none',
              fontSize: '0.875rem'
            }}
          >
            Cancel
          </Button>
          <Box
            component="button"
            onClick={handleDeleteTeam}
            disabled={deleting}
            sx={{
              ...styles.bodyFont,
              backgroundColor: deleting ? '#D1D5DB' : '#b91c1c',
              color: deleting ? '#9CA3AF' : 'white',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              px: 2,
              py: 0.75,
              borderRadius: '9999px',
              border: 'none',
              cursor: deleting ? 'not-allowed' : 'pointer',
              display: 'inline-block',
              '&:hover': {
                backgroundColor: deleting ? '#D1D5DB' : '#991B1B'
              },
              '&:active': {
                backgroundColor: '#b91c1c'
              },
              '&:disabled': {
                backgroundColor: '#D1D5DB',
                color: '#475569',
                cursor: 'not-allowed'
              }
            }}
          >
            {deleting ? 'Deleting...' : 'Delete Project'}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletEnhancementConfig;
